<template>
	<div class="bill-list-page">
		<!-- 搜索查询区 -->
		<el-card class="search-card" shadow="hover">
			<el-form :inline="true" :model="queryParams" class="search-form">
				<el-form-item label="创建时间">
					<el-date-picker
						v-model="queryTime"
						type="daterange"
						range-separator="至"
						start-placeholder="开始日期"
						end-placeholder="结束日期"
						value-format="YYYY-MM-DD HH:mm:ss"
						:default-time="[new Date(0, 0, 0, 0, 0, 0), new Date(0, 0, 0, 23, 59, 59)]"
						style="width: 320px;"
					/>
				</el-form-item>
				<el-form-item>
					<el-button type="primary" @click="handleSearch">
						<el-icon class="btn-icon"><Search /></el-icon>
						查询
					</el-button>
					<el-button @click="handleReset">
						<el-icon class="btn-icon"><Refresh /></el-icon>
						重置
					</el-button>
				</el-form-item>
			</el-form>
		</el-card>

		<!-- 数据表格区 -->
		<el-card class="table-card" shadow="never">
			<el-table 
				:data="billList" 
				v-loading="tableLoading"
				style="width: 100%" 
				stripe
				border
			>
				<el-table-column prop="billId" label="账单编号" min-width="150" align="center" />
				<el-table-column prop="creationTime" label="账单生成日期" min-width="160" align="center" />
				<el-table-column prop="billName" label="账单名称" min-width="200" show-overflow-tooltip />
				<el-table-column prop="customerName" label="结算对象" min-width="150" show-overflow-tooltip />
				
				<!-- 金额格式化 -->
				<el-table-column prop="totalAmount" label="总金额" min-width="120" align="right">
					<template #default="scope">
						<span class="amount-text">¥ {{ formatAmount(scope.row.totalAmount) }}</span>
					</template>
				</el-table-column>
				
				<!-- 操作列 -->
				<el-table-column label="操作" width="180" align="center" fixed="right">
					<template #default="scope">
						<el-button link type="primary" @click="openDetail(scope.row)">
							<el-icon class="btn-icon"><View /></el-icon>
							查看详情
						</el-button>
						<el-button link type="success" @click="exportTable(scope.row)">
							<el-icon class="btn-icon"><Download /></el-icon>
							导出表格
						</el-button>
					</template>
				</el-table-column>
				
				<template #empty>
					<el-empty description="暂无账单数据" />
				</template>
			</el-table>

			<!-- 分页组件 -->
			<div class="pagination-container">
				<el-pagination
					v-model:current-page="currentPage"
					v-model:page-size="pageSize"
					:page-sizes="[10, 20, 50, 100]"
					:total="total"
					layout="total, sizes, prev, pager, next, jumper"
					@size-change="handleSizeChange"
					@current-change="handleCurrentChange"
				/>
			</div>
		</el-card>
	</div>
</template>

<script>
	import { Search, Refresh, View, Download } from '@element-plus/icons-vue';
	import { ElMessageBox } from 'element-plus';

	export default {
		// 注册组件以消除 "not used" 警告，因为我们在模板中使用了 <Search /> 等标签
		components: {
			Search,
			Refresh,
			View,
			Download
		},
		data() {
			return {
				tableLoading: false,
				billList: [],
				
				// 查询参数
				queryTime: [],
				currentPage: 1,
				pageSize: 10,
				total: 0,
			};
		},
		created() {
			this.initDefaultTime();
			this.getBillList();
		},
		methods: {
			// 初始化默认时间（最近7天）
			initDefaultTime() {
				const end = new Date();
				const start = new Date();
				start.setDate(start.getDate() - 7);
				
				// 格式化为 YYYY-MM-DD HH:mm:ss
				const formatDate = (date, isStart) => {
					const y = date.getFullYear();
					const m = String(date.getMonth() + 1).padStart(2, '0');
					const d = String(date.getDate()).padStart(2, '0');
					const time = isStart ? '00:00:00' : '23:59:59';
					return `${y}-${m}-${d} ${time}`;
				};
				
				this.queryTime = [formatDate(start, true), formatDate(end, false)];
			},

			// 获取账单列表
			getBillList() {
				this.tableLoading = true;
				const [startTime, endTime] = this.queryTime || [];
				
				this.apiService.post({
					modules: "getBillList",
					content: {
						startTime,
						endTime,
						pageNum: this.currentPage,
						pageSize: this.pageSize
					},
				})
				.then((res) => {
					if (res.status) {
						this.billList = res.data.billList || [];
						this.total = res.data.total || 0;
					} else {
						this.$message.error(res.msg || '获取数据失败');
					}
				})
				.catch(() => {
					this.$message.error('网络请求异常');
				})
				.finally(() => {
					this.tableLoading = false;
				});
			},

			// 查询
			handleSearch() {
				this.currentPage = 1;
				this.getBillList();
			},

			// 重置
			handleReset() {
				this.initDefaultTime();
				this.currentPage = 1;
				this.getBillList();
			},

			// 分页大小改变
			handleSizeChange(val) {
				this.pageSize = val;
				this.currentPage = 1;
				this.getBillList();
			},

			// 当前页改变
			handleCurrentChange(val) {
				this.currentPage = val;
				this.getBillList();
			},

			// 查看详情
			openDetail(row) {
				this.$router.push({
					path: "/finance/bill/detail",
					query: { billId: row.billId }
				});
			},

			// 导出表格
			exportTable(row) {
				this.tableLoading = true;
				this.apiService.post({
					modules: "exportBillTable",
					content: { billId: row.billId }
				})
				.then((res) => {
					if (res.status) {
						ElMessageBox.alert(
							'已生成下载任务，您可在下载中心查看进度并下载文件。', 
							'导出成功', 
							{
								confirmButtonText: '前往下载中心',
								type: 'success',
								callback: (action) => {
									if (action === 'confirm') {
										this.$router.push('/download/center');
									}
								}
							}
						);
					}
				})
				.finally(() => {
					this.tableLoading = false;
				});
			},

			// 金额格式化
			formatAmount(amount) {
				if (amount === null || amount === undefined) return '0.00';
				return Number(amount).toLocaleString('zh-CN', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2
				});
			}
		}
	};
</script>

<style scoped lang="scss">
	.bill-list-page {
		padding: 20px;
		background-color: #f5f7fa;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.search-card {
		.search-form {
			display: flex;
			align-items: center;
		}
	}

	.table-card {
		flex: 1;
		display: flex;
		flex-direction: column;

		// 自定义表头样式
		:deep(.el-table__header-wrapper) {
			th.el-table__cell {
				background-color: #f5f7fa !important;
				color: #303133 !important;
				font-weight: 600 !important;
				font-size: 14px;
			}
		}

		.amount-text {
			color: #e6a23c;
			font-weight: 500;
		}
		
		.btn-icon {
			margin-right: 4px;
			vertical-align: middle;
		}

		.pagination-container {
			margin-top: 20px;
			display: flex;
			justify-content: flex-end;
		}
	}
</style>